import { ColumnDef } from '@tanstack/react-table';
import React from 'react';
import type { ColumnDescription } from 'react-bootstrap-table-next';

/*
 * Yes, field is undefined because it is invariably never used. Each column
 * defines its own onSort and thus already knows what field to put.
 */
export type LegacySortFn = (field: undefined, order?: 'asc' | 'desc') => void;

export function toColumnDefs(
  columns: ColumnDescription[]
): ColumnDef<any, any>[] {
  return columns.map((col, index) => {
    const isDummy = col.isDummyField || col.dataField === '';
    const id = isDummy ? `dummy_${index}` : col.dataField;
    const legacyOnSort = (col as any).onSort as LegacySortFn | undefined;
    return {
      id,
      accessorKey: isDummy ? undefined : col.dataField,
      header: (info) =>
        col.headerFormatter
          ? col.headerFormatter(col, info.header.index, {
              sortElement: undefined, // NOTE: these might not be undefined but headerFormatter is never used.
              filterElement: undefined,
            })
          : (col.text ?? col.dataField),
      cell: (info) => {
        const row = info.row.original;
        const value = info.getValue();
        if (typeof col.formatter === 'function') {
          return col.formatter(value, row, info.row.index);
        }
        return value as React.ReactNode;
      },
      enableSorting: !isDummy && Boolean((col as any).sort),
      meta: {
        align: getAlignAsString(col.align as string),
        headerAlign: getAlignAsString(col.headerAlign as string),
        // Legacy sorting bridge (react-bootstrap-table-next column API)
        legacyOnSort,
      },
    };
  });
}

function getAlignAsString(
  align: string
): 'left' | 'center' | 'right' | undefined {
  return ['left', 'center', 'right'].includes(align)
    ? (align as 'left' | 'center' | 'right')
    : undefined;
}

export function makeGetRowIdFromKeyField(keyField: string) {
  return (row: any, idx: number) => {
    const id = row?.[keyField];
    return id != null ? String(id) : String(idx);
  };
}
